import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { DroneReport } from '../data-models/drone-report.model';
import { DroneReportCreateRequest } from '../request-models/drone-report-create-request.model';
import { DroneReportFilters } from '../request-models/drone-report-filters.model';
import { DroneReportJob } from '../response-models/drone-report-job.model';
import { DroneReportWithJobs } from '../response-models/drone-report-with-jobs.model';
import { OrganizationOption } from '../response-models/organization-option.model';
import { Page } from '../response-models/page.model';
import { isRecord } from '../utils/is-record';
import { countOrZero, idString, nullableNumber, nullableString } from '../utils/wire-value-utils';
import { OrganizationService } from './organization.service';

/**
 * Drone incident-history reports across organizations: `/admin/drone-reports[/:id[/regenerate|/retry]]`
 * (hub `admin-drone-reports-controller.ts`). Paged with `limit`. Every method rejects with an `ApiError`.
 */
@Injectable({
  providedIn: 'root'
})
export class DroneReportService extends BaseService<DroneReport> {
  private readonly organizationService = inject(OrganizationService);

  constructor() {
    super('admin/drone-reports', DroneReport);
  }

  /** The org picker (ADN-2471a): every organization as `{ id, name }`. */
  async listOrganizations(): Promise<OrganizationOption[]> {
    const page = await firstValueFrom(this.organizationService.getOptions());
    return page.items;
  }

  /** GET /admin/drone-reports with the filters that are set. A drone filter needs both id and source. */
  listReports(filters: DroneReportFilters): Promise<Page<DroneReport>> {
    return firstValueFrom(this.getPage({ query: toListQuery(filters) }));
  }

  /** POST /admin/drone-reports: creates the report and queues its generation (202). */
  async generateReport(request: DroneReportCreateRequest): Promise<void> {
    const body: DroneReportCreateRequest = {
      droneId: request.droneId,
      droneSource: request.droneSource,
      scope: request.scope,
      startDate: request.startDate || undefined,
      endDate: request.endDate || undefined,
    };
    await firstValueFrom(this.request('POST', {}, () => undefined, body));
  }

  /** GET /admin/drone-reports/:id: the report (with a download URL when ready) and its queue jobs. */
  getReport(id: string): Promise<DroneReportWithJobs | null> {
    return firstValueFrom(this.request('GET', { id }, toReportWithJobs));
  }

  async deleteReport(id: string): Promise<void> {
    await firstValueFrom(this.delete(id));
  }

  /** POST /admin/drone-reports/:id/regenerate */
  async regenerateReport(id: string): Promise<void> {
    await firstValueFrom(this.request('POST', { id, path: 'regenerate' }, () => undefined, {}));
  }

  /** POST /admin/drone-reports/:id/retry (failed reports only). */
  async retryReport(id: string): Promise<void> {
    await firstValueFrom(this.request('POST', { id, path: 'retry' }, () => undefined, {}));
  }
}

function toListQuery(filters: DroneReportFilters): Record<string, string | number | undefined> {
  const hasDrone = !!filters.droneId && !!filters.droneSource;
  return {
    organizationId: filters.organizationId || undefined,
    droneId: hasDrone ? filters.droneId : undefined,
    droneSource: hasDrone ? filters.droneSource : undefined,
    status: filters.status || undefined,
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
    search: filters.search?.trim() || undefined,
    page: filters.page,
    limit: filters.pageSize,
  };
}

function toReportWithJobs(data: unknown): DroneReportWithJobs | null {
  if (!isRecord(data) || !isRecord(data['report'])) {
    return null;
  }
  const report = new DroneReport(data['report']) as DroneReportWithJobs;
  report.jobs = Array.isArray(data['jobs']) ? data['jobs'].map(toJob) : [];
  return report;
}

function toJob(item: unknown): DroneReportJob {
  const source = isRecord(item) ? item : {};
  return {
    id: idString(source['id']),
    name: nullableString(source['name']) ?? '',
    status: nullableString(source['status']) ?? '',
    progress: countOrZero(source['progress']),
    attemptsMade: countOrZero(source['attemptsMade']),
    createdAt: nullableNumber(source['createdAt']) ?? undefined,
    processedAt: nullableNumber(source['processedAt']) ?? undefined,
    finishedAt: nullableNumber(source['finishedAt']) ?? undefined,
    failedReason: nullableString(source['failedReason']) ?? undefined,
  };
}
